import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('dronemaps_token'));

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      localStorage.removeItem('dronemaps_token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('dronemaps_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (payload) => {
    // Accept a plain object so we don't have to grow a 10-arg positional
    // signature every time onboarding adds a field. Callers pass e.g.
    // { name, email, password, country, companyName, uasOperatorNumber, firstDrone }.
    const body = {
      name: payload.name,
      email: payload.email,
      password: payload.password,
      country: payload.country,
      // profile_type is now always "pro" — amateur was retired in Feb 2026
      // when we pivoted the onboarding to B2B-only. Keep sending it
      // explicitly so any downstream analytics that key on it stay green.
      profile_type: 'pro',
      company_name: payload.companyName || null,
      uas_operator_number: payload.uasOperatorNumber || null,
      first_drone: payload.firstDrone || null,
    };
    const response = await axios.post(`${API_URL}/api/auth/register`, body);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('dronemaps_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('dronemaps_token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const value = {
    user,
    setUser,  // exposed so components can inject freshly-updated user data
              // (e.g. ProfileCompletionBanner after PATCH /auth/me/profile)
              // without a full refetch.
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
